/**
 * Changing what a dashboard holds, inside the cached lists of it.
 *
 * These are the pure halves of every change to a board: the widget list and the
 * data source list are plain arrays under `qk.widgets` and `qk.dataSources`, and
 * both are written by two callers -- the tab that made the change, and the
 * realtime handler for the tab that did not. Keeping the arithmetic here means
 * the two cannot disagree about what "add a widget" does.
 *
 * Each returns the array it was given when nothing matched, so a caller can
 * write back only what actually changed.
 */
import type { DashboardDataSource, Widget } from '@/api/types'

/**
 * Adds a widget, or replaces the one already held with that id.
 *
 * Upsert rather than insert, for the reason every other list's is: a reconnect
 * replays what was missed, and a replay does not know what was applied.
 */
export function withWidget(held: readonly Widget[], widget: Widget): Widget[] {
  const index = held.findIndex((one) => one.id === widget.id)
  if (index === -1) return [...held, widget]

  const next = [...held]
  next[index] = widget
  return next
}

/** Takes a widget out of the list, by id. */
export function withoutWidget(held: readonly Widget[], widgetId: number): Widget[] {
  return held.filter((one) => one.id !== widgetId)
}

/**
 * Renumbers the widgets to the sequence `ids` gives, as the server does: one it
 * leaves out goes last.
 */
export function inOrder(held: readonly Widget[], ids: readonly number[]): Widget[] {
  const position = new Map(ids.map((id, index) => [id, index + 1]))
  return held.map((one) => ({
    ...one,
    order: String(position.get(one.id) ?? ids.length + 1),
  }))
}

/** Replaces one data source in the list, leaving every other one alone. */
export function withDataSource(
  held: readonly DashboardDataSource[],
  source: DashboardDataSource,
): DashboardDataSource[] {
  return held.map((one) => (one.id === source.id ? source : one))
}
